import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {selectManagedAccountIds} from 'src/modules/main/services/user-info-slice';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {selectAccountLicense} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {getAdminAccessRights} from 'src/openapi/admin/apis/GetAdminAccessRightsApi';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';
import {useAppDispatch} from 'src/store/hooks';

import {ACCESS_RIGHTS, type AccessRight} from './constants';
import {selectAccessRights} from './services/access-rights-slice';

import type {LicenseType} from 'src/openapi/admin/models/LicenseType';

export const useAdminAccessRight = () => {
    const apiDispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);

    const adminAccessRights = useSelector(selectAccessRights);
    const managedAccountIds = useSelector(selectManagedAccountIds);
    const {adminAccountIds, freeNewsletterAccountIds} = useSystemAccounts();
    const {license: activeLicense} = useSelector(selectAccountDetails);
    const license = useSelector(selectAccountLicense);

    useEffect(() => {
        const loadAdminAccessRight = async () => {
            setIsLoading(true);
            await apiDispatch(getAdminAccessRights());
            setIsLoading(false);
        };

        if (!adminAccessRights?.length) {
            loadAdminAccessRight();
        } else {
            // If access rights are already loaded, set loading to false
            setIsLoading(false);
        }
    }, [adminAccessRights, apiDispatch]);

    const isAdminAccount = useCallback((accountId: number) => {
        return adminAccountIds.includes(accountId);
    }, [adminAccountIds]);

    const isFreeNewsletterAccount = useCallback((accountId: number) => {
        return freeNewsletterAccountIds.includes(accountId);
    }, [freeNewsletterAccountIds]);

    const hasAccess = useCallback((accessRight: string) => {
        return !!adminAccessRights?.some(right => right === accessRight);
    }, [adminAccessRights]);

    const globalAccountManagement = useCallback((accountId: number | null, permission: AccessRight) => {
        if (hasAccess(ACCESS_RIGHTS.GLOBAL_ACCOUNT_MANAGEMENT)) {
            return hasAccess(permission);
        } else if (accountId && managedAccountIds && managedAccountIds.includes(accountId)) {
            return hasAccess(permission);
        } else {
            return false;
        }
    }, [managedAccountIds, hasAccess]);

    const systemAccountManagement = useCallback((accountId: number) => {
        if (isFreeNewsletterAccount(accountId) || isAdminAccount(accountId)) {
            return hasAccess(ACCESS_RIGHTS.MANAGE_SYSTEM_ACCOUNTS);
        }

        return true;
    }, [isFreeNewsletterAccount, isAdminAccount, hasAccess]);

    const paidLicenseManagement = useCallback(() => {
        if (license?.typeCode === LicenseTypes.CN && hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT)) {
            return true;
        }

        return hasAccess(ACCESS_RIGHTS.ALL_LICENSE_TYPES_ACCESS) ||
            [LicenseTypes.TR, LicenseTypes.RT].includes(license?.typeCode as LicenseType);
    }, [license?.typeCode, hasAccess]);

    const activePaidLicenseManagement = useCallback(() => {
        return hasAccess(ACCESS_RIGHTS.ALL_LICENSE_TYPES_ACCESS) || !(
            ![LicenseTypes.TR, LicenseTypes.RT].includes(activeLicense?.typeCode as LicenseType) &&
            activeLicense?.isActive
        );
    }, [activeLicense, hasAccess]);

    const canCreateLicense = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_CREATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, systemAccountManagement, hasAccess]);

    const canCreateLicenseFromExisted = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_CREATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
            () => paidLicenseManagement(),
        ].every(check => check());
    }, [globalAccountManagement, systemAccountManagement, paidLicenseManagement, hasAccess]);

    const canEditLicense = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
            () => paidLicenseManagement(),
        ].every(check => check());
    }, [paidLicenseManagement, systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditLicenseNotes = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
        ].every(check => check());
    }, [systemAccountManagement, hasAccess]);

    const canViewAccountLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_LIMITS_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_LIMITS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccessToModules = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountBilling = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.LICENSE_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canCreateAccount = useMemo(() => hasAccess(ACCESS_RIGHTS.ACCOUNT_SCREEN_CREATE), [hasAccess]);

    const canEditAccount = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountNotes = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
        ].every(check => check());
    }, [systemAccountManagement, hasAccess]);

    const canEditCrm = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.CRM_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditSso = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.SSO_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditMicrosites = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.MICROSITES_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canViewAccountConnectorLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_CONNECTOR_LIMITS_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountConnectorLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_CONNECTOR_LIMITS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canViewUserConnectorLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_CONNECTOR_LIMITS_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canEditUserConnectorLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_CONNECTOR_LIMITS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canViewAccountMcp = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_DD_MCP_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountMcp = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_DD_MCP_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditApiV2 = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.API_V2_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountEntitlements = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_ENTITLEMENTS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canDeleteAccount = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_SCREEN_DELETE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canManageUserRole = useCallback((accountId: number) => {
        return isAdminAccount(accountId) && hasAccess(ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE);
    }, [isAdminAccount, hasAccess]);

    const canEditUserNotes = useCallback(() => {
        const permission = ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
        ].every(check => check());
    }, [hasAccess]);

    const canEditUser = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_SCREEN_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canEditUserLimits = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_LIMITS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canChangeUserAccount = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.CHANGE_USER_ACCOUNT as AccessRight;
        const checks = [
            () => hasAccess(permission),
        ];

        if (!isFreeNewsletterAccount(accountId)) {
            checks.push(() => globalAccountManagement(accountId, permission));
        }

        return checks.every(check => check());
    }, [globalAccountManagement, isFreeNewsletterAccount, hasAccess]);

    const canDeleteUser = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_SCREEN_DELETE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canCreateUser = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_SCREEN_CREATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
            () => activePaidLicenseManagement(),
        ].every(check => check());
    }, [globalAccountManagement, activePaidLicenseManagement, hasAccess]);

    const canBulkImportUser = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_USER_IMPORT as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
            () => activePaidLicenseManagement(),
        ].every(check => check());
    }, [globalAccountManagement, activePaidLicenseManagement, hasAccess]);

    const canBulkChangeUsersAccount = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_USER_ACCOUNT_CHANGE as AccessRight;
        const checks = [
            () => hasAccess(permission),
            () => activePaidLicenseManagement(),
        ];

        if (!freeNewsletterAccountIds.includes(accountId)) {
            checks.push(() => globalAccountManagement(accountId, permission));
        }

        return checks.every(check => check());
    }, [globalAccountManagement, activePaidLicenseManagement, hasAccess, freeNewsletterAccountIds]);

    const canBulkSetNewsletters = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_NEWSLETTER_SUBSCRIPTION_UPDATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);


    const canBulkSetUserState = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_USER_STATE_UPDATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canBulkUpdateDeviceRegistration = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_UPDATE_DEVICE_REGISTRATION as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canBulkUpdateMfa = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_MFA_SETTINGS_UPDATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canEditUserEntitlements = useCallback((accountId: number | null) => {
        const permission = ACCESS_RIGHTS.USER_ENTITLEMENTS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [globalAccountManagement, hasAccess]);

    const canViewUserProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_PRODUCTS_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditUserProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_PRODUCTS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canDeleteUserProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_PRODUCTS_DELETE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canCreateUserProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.USER_PRODUCTS_CREATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canViewAccountProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_PRODUCTS_VIEW as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canEditAccountProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_PRODUCTS_EDIT as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canDeleteAccountProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_PRODUCTS_DELETE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canCreateAccountProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.ACCOUNT_PRODUCTS_CREATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    const canBulkUpdateProducts = useCallback((accountId: number) => {
        const permission = ACCESS_RIGHTS.BULK_PRODUCTS_UPDATE as AccessRight;

        return [
            () => hasAccess(permission),
            () => systemAccountManagement(accountId),
            () => globalAccountManagement(accountId, permission),
        ].every(check => check());
    }, [systemAccountManagement, globalAccountManagement, hasAccess]);

    return {
        isLoading,
        hasAccess,

        isAdminAccount,

        canCreateLicense,
        canEditLicense,
        canEditLicenseNotes,
        canCreateLicenseFromExisted,

        canCreateAccount,
        canEditAccount,
        canEditAccountNotes,
        canEditCrm,
        canEditSso,
        canEditApiV2,
        canEditMicrosites,
        canViewAccountMcp,
        canEditAccountMcp,
        canViewAccountLimits,
        canEditAccountLimits,
        canViewAccountConnectorLimits,
        canEditAccountConnectorLimits,
        canViewUserConnectorLimits,
        canEditUserConnectorLimits,
        canEditAccountBilling,
        canEditAccessToModules,
        canEditAccountEntitlements,
        canDeleteAccount,

        canCreateUser,
        canEditUser,
        canEditUserLimits,
        canChangeUserAccount,
        canEditUserEntitlements,
        canEditUserNotes,
        canDeleteUser,

        canBulkImportUser,
        canBulkChangeUsersAccount,
        canBulkSetNewsletters,
        canBulkSetUserState,
        canBulkUpdateDeviceRegistration,
        canBulkUpdateMfa,
        canBulkUpdateProducts,

        canManageUserRole,

        canViewUserProducts,
        canViewAccountProducts,
        canEditUserProducts,
        canEditAccountProducts,
        canCreateUserProducts,
        canCreateAccountProducts,
        canDeleteUserProducts,
        canDeleteAccountProducts,
    };
};
